import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtDateTime } from '@/utils/dateFmt';
import { toast } from 'sonner';
import { Star, Plus, Trash, PencilSimple, Power, CheckCircle, XCircle, Clock } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ["Super Pré Mamãs", "Super Barriguinhas", "Super Mamãs", "Super Bebés", "Nutrição Pediátrica", "Emagrecimento", "Geral"];
const WEEKDAYS = [
  { value: '1', label: 'Segunda' }, { value: '2', label: 'Terça' }, { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' }, { value: '5', label: 'Sexta' }, { value: '6', label: 'Sábado' }, { value: '0', label: 'Domingo' },
];

function SessoesTab() {
  const [sessions, setSessions] = useState([]);
  const [codeFilter, setCodeFilter] = useState('');
  const [convertedFilter, setConvertedFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ converted: '', converted_program: '', notes: '' });

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (codeFilter) params.set('code', codeFilter);
      if (convertedFilter) params.set('converted', convertedFilter);
      const { data } = await axios.get(`${API}/api/free-sessions?${params}`);
      setSessions(data || []);
    } catch { toast.error('Erro ao carregar sessões'); } finally { setLoading(false); }
  }, [codeFilter, convertedFilter]);
  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const openOutcome = (s) => { setEditing(s); setOutcomeForm({ converted: s.converted === true ? 'sim' : s.converted === false ? 'nao' : '', converted_program: s.converted_program || '', notes: s.notes || '' }); };

  const saveOutcome = async () => {
    try {
      await axios.put(`${API}/api/free-sessions/${editing.id}`, {
        converted: outcomeForm.converted === 'sim' ? true : outcomeForm.converted === 'nao' ? false : null,
        converted_program: outcomeForm.converted === 'sim' ? outcomeForm.converted_program : '',
        notes: outcomeForm.notes,
      });
      toast.success('Resultado registado');
      setEditing(null); fetchSessions();
    } catch { toast.error('Erro ao guardar'); }
  };

  const badge = (s) => {
    if (s.converted === true) return <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle size={11} /> Converteu {s.converted_program && `· ${s.converted_program}`}</span>;
    if (s.converted === false) return <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><XCircle size={11} /> Não converteu</span>;
    return <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock size={11} /> Pendente</span>;
  };

  return (
    <div data-testid="sessoes-gratuitas-tab">
      <div className="flex gap-2 mb-4 flex-wrap">
        <Input value={codeFilter} onChange={e => setCodeFilter(e.target.value.toUpperCase())} placeholder="Filtrar por código (ex: CPP)" className="w-56 rounded-xl border-brand-border" data-testid="filter-code-input" />
        <Select value={convertedFilter || '_all'} onValueChange={v => setConvertedFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-48" data-testid="filter-converted-select"><SelectValue placeholder="Estado de conversão" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">Todos</SelectItem><SelectItem value="sim">Converteu</SelectItem><SelectItem value="nao">Não converteu</SelectItem><SelectItem value="pendente">Pendente</SelectItem></SelectContent>
        </Select>
      </div>
      {loading ? <p className="text-center py-10 text-brand-muted">A carregar...</p> : sessions.length === 0 ? (
        <div className="text-center py-14 bg-white border border-dashed border-brand-border rounded-2xl" data-testid="no-free-sessions"><Star size={40} className="mx-auto text-brand-placeholder mb-3" weight="duotone" /><p className="text-brand-muted text-sm">Sem sessões gratuitas registadas.</p></div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.id} className="bg-white border border-brand-border rounded-2xl p-3.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`free-session-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-brand-text">{s.lead_name} <span className="text-xs text-brand-muted">· {s.partnership_code} ({s.partner_name})</span></p>
                <p className="text-[11px] text-brand-muted">{fmtDateTime(s.date, s.time)} · {s.nutri_name} · {s.lead_email} · {s.lead_phone}</p>
              </div>
              {badge(s)}
              <button onClick={() => openOutcome(s)} className="text-xs text-brand-green font-medium hover:underline" data-testid={`mark-outcome-btn-${s.id}`}>Marcar resultado</button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Resultado da sessão</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1.5 block">Converteu?</Label>
              <div className="flex gap-2">
                {[['sim', 'Sim'], ['nao', 'Não'], ['', 'Pendente']].map(([v, l]) => (
                  <button key={v || 'p'} onClick={() => setOutcomeForm(f => ({ ...f, converted: v }))} className={`px-3 py-1.5 rounded-full text-xs font-medium ${outcomeForm.converted === v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`outcome-${v || 'pendente'}`}>{l}</button>
                ))}
              </div>
            </div>
            {outcomeForm.converted === 'sim' && (
              <div>
                <Label className="text-xs text-brand-muted mb-1.5 block">Para que programa</Label>
                <Select value={outcomeForm.converted_program} onValueChange={v => setOutcomeForm(f => ({ ...f, converted_program: v }))}>
                  <SelectTrigger data-testid="outcome-program-select"><SelectValue placeholder="Escolhe o programa" /></SelectTrigger>
                  <SelectContent>{PROGRAMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1.5 block">Notas</Label><Textarea value={outcomeForm.notes} onChange={e => setOutcomeForm(f => ({ ...f, notes: e.target.value }))} className="rounded-xl border-brand-border resize-none" rows={3} data-testid="outcome-notes-input" /></div>
            <button onClick={saveOutcome} className="w-full bg-brand-green text-white rounded-full py-2.5 font-medium" data-testid="save-outcome-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CodigosTab() {
  const confirmDelete = useConfirmDelete();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', partner_name: '', max_sessions: 10, end_date: '' });

  const fetchCodes = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/partnership-codes`); setCodes(data || []); } catch { toast.error('Erro ao carregar códigos'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openNew = () => { setEditing(null); setForm({ code: '', partner_name: '', max_sessions: 10, end_date: '' }); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ code: c.code, partner_name: c.partner_name, max_sessions: c.max_sessions, end_date: c.end_date || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.partner_name.trim() || (!editing && !form.code.trim())) { toast.error('Preenche os campos obrigatórios'); return; }
    try {
      if (editing) await axios.put(`${API}/api/admin/partnership-codes/${editing.id}`, { partner_name: form.partner_name, max_sessions: Number(form.max_sessions), end_date: form.end_date });
      else await axios.post(`${API}/api/admin/partnership-codes`, { code: form.code, partner_name: form.partner_name, max_sessions: Number(form.max_sessions), end_date: form.end_date });
      toast.success(editing ? 'Código atualizado' : 'Código criado');
      setShowForm(false); fetchCodes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const toggleActive = async (c) => { try { await axios.put(`${API}/api/admin/partnership-codes/${c.id}`, { active: !c.active }); fetchCodes(); } catch { toast.error('Erro'); } };
  const del = async (c) => {
    const ok = await confirmDelete({ description: `Vai apagar o código de parceria "${c.code}" (${c.partner_name}).` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/admin/partnership-codes/${c.id}`); toast.success('Código eliminado'); fetchCodes(); } catch { toast.error('Erro'); }
  };

  return (
    <div data-testid="codigos-parceria-tab">
      <div className="flex justify-end mb-4"><button onClick={openNew} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-2" data-testid="new-code-btn"><Plus size={14} /> Novo código</button></div>
      {loading ? <p className="text-center py-10 text-brand-muted">A carregar...</p> : codes.length === 0 ? (
        <div className="text-center py-14 bg-white border border-dashed border-brand-border rounded-2xl"><p className="text-brand-muted text-sm">Sem códigos de parceria criados.</p></div>
      ) : (
        <div className="space-y-2">
          {codes.map(c => (
            <div key={c.id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`code-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-brand-text">{c.code} <span className="text-xs text-brand-muted">· {c.partner_name}</span></p>
                <p className="text-[11px] text-brand-muted">{c.sessions_used}/{c.max_sessions} sessões usadas{c.end_date ? ` · termina ${c.end_date}` : ''}</p>
                {!c.is_valid && <p className="text-[11px] text-red-500">{c.invalid_reason}</p>}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.active ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c.active ? 'Ativo' : 'Desativado'}</span>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`edit-code-${c.id}`}><PencilSimple size={14} /></button>
                <button onClick={() => toggleActive(c)} className="p-1.5 text-brand-placeholder hover:text-amber-500" data-testid={`toggle-code-${c.id}`}><Power size={14} /></button>
                <button onClick={() => del(c)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`delete-code-${c.id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editing ? 'Editar código' : 'Novo código de parceria'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && <div><Label className="text-xs text-brand-muted mb-1 block">Código</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ex: CPP" className="rounded-xl border-brand-border" data-testid="code-code-input" /></div>}
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome da parceria</Label><Input value={form.partner_name} onChange={e => setForm({ ...form, partner_name: e.target.value })} className="rounded-xl border-brand-border" data-testid="code-partner-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Limite máximo de sessões</Label><Input type="number" min="1" value={form.max_sessions} onChange={e => setForm({ ...form, max_sessions: e.target.value })} className="rounded-xl border-brand-border" data-testid="code-max-sessions-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Data de fim <span className="text-[10px] text-brand-placeholder">(opcional)</span></Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="rounded-xl border-brand-border" data-testid="code-end-date-input" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-2.5 font-medium" data-testid="save-code-btn">{editing ? 'Guardar' : 'Criar código'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DisponibilidadeTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const fetchAvail = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/free-session-availability`);
      setList(data || []);
      const d = {}; (data || []).forEach(n => { d[n.nutri_id] = n.weekday_times || {}; }); setDrafts(d);
    } catch { toast.error('Erro ao carregar disponibilidade'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAvail(); }, [fetchAvail]);

  const toggleTime = (nutriId, wd, time) => {
    setDrafts(prev => {
      const cur = new Set(prev[nutriId]?.[wd] || []);
      cur.has(time) ? cur.delete(time) : cur.add(time);
      return { ...prev, [nutriId]: { ...prev[nutriId], [wd]: Array.from(cur).sort() } };
    });
  };
  const addTimeSlot = (nutriId, wd) => {
    const t = window.prompt('Hora (formato HH:MM, ex: 10:00)');
    if (!t || !/^\d{2}:\d{2}$/.test(t)) return;
    toggleTime(nutriId, wd, t);
  };
  const save = async (nutriId) => {
    try { await axios.put(`${API}/api/admin/free-session-availability/${nutriId}`, { weekday_times: drafts[nutriId] || {} }); toast.success('Disponibilidade guardada'); fetchAvail(); } catch { toast.error('Erro ao guardar'); }
  };

  if (loading) return <p className="text-center py-10 text-brand-muted">A carregar...</p>;
  return (
    <div className="space-y-5" data-testid="disponibilidade-tab">
      <p className="text-xs text-brand-muted">Duração fixa de 20 minutos por sessão gratuita ("{`Sessão Nutrir a Super Mãe`}"). Define os dias/horas em que cada nutricionista está disponível, agenda própria, separada das consultas normais.</p>
      {list.map(n => (
        <div key={n.nutri_id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`avail-card-${n.nutri_id}`}>
          <p className="font-heading font-medium text-brand-text mb-3">{n.name}</p>
          <div className="space-y-2">
            {WEEKDAYS.map(wd => (
              <div key={wd.value} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-brand-muted w-20">{wd.label}</span>
                {(drafts[n.nutri_id]?.[wd.value] || []).map(t => (
                  <button key={t} onClick={() => toggleTime(n.nutri_id, wd.value, t)} className="text-[11px] bg-brand-green/10 text-brand-green px-2 py-1 rounded-full" data-testid={`avail-time-${n.nutri_id}-${wd.value}-${t}`}>{t} ✕</button>
                ))}
                <button onClick={() => addTimeSlot(n.nutri_id, wd.value)} className="text-[11px] text-brand-muted border border-dashed border-brand-border rounded-full px-2 py-1 hover:border-brand-green" data-testid={`add-time-${n.nutri_id}-${wd.value}`}>+ hora</button>
              </div>
            ))}
          </div>
          <button onClick={() => save(n.nutri_id)} className="mt-3 bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid={`save-avail-${n.nutri_id}`}>Guardar</button>
        </div>
      ))}
    </div>
  );
}

export default function SessoesGratuitasPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="fade-in" data-testid="sessoes-gratuitas-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center"><Star size={22} className="text-amber-600" weight="duotone" /></div>
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Sessões Gratuitas</h2>
          <p className="text-sm text-brand-muted mt-0.5">"Sessão Nutrir a Super Mãe" · marcações via código de parceria em /agendarconsulta</p>
        </div>
      </div>
      <Tabs defaultValue="sessoes" className="w-full">
        <TabsList className="bg-white border border-brand-border rounded-2xl p-1 mb-5" data-testid="marketing-tabs">
          <TabsTrigger value="sessoes" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-sessoes">Sessões</TabsTrigger>
          {isAdmin && <TabsTrigger value="codigos" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-codigos">Códigos de Parceria</TabsTrigger>}
          {isAdmin && <TabsTrigger value="disponibilidade" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-disponibilidade">Disponibilidade</TabsTrigger>}
        </TabsList>
        <TabsContent value="sessoes"><SessoesTab /></TabsContent>
        {isAdmin && <TabsContent value="codigos"><CodigosTab /></TabsContent>}
        {isAdmin && <TabsContent value="disponibilidade"><DisponibilidadeTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
